import React, { useState, useEffect } from "react";
import firebase from 'firebase/compat/app';
import TrustScoreIndicator from "./TrustScoreIndicator";

const TrustScoreContainer = ({ email, name }) => {
  const [trustData, setTrustData] = useState(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const [error, setError] = useState(null);

  // Reference to the Cloud Function
  const checkEmailReputationFunction = firebase.functions().httpsCallable('checkEmailReputation');

  // First, check if we have cached data in Firestore
  useEffect(() => {
    if (email) {
      const fetchCachedData = async () => {
        try {
          const db = firebase.firestore();
          const emailVerificationDoc = await db
            .collection('emailVerifications')
            .doc(email.toLowerCase())
            .get();
          
          if (emailVerificationDoc.exists) {
            const data = emailVerificationDoc.data();
            
            // If the data is less than 7 days old, use it
            const cachedTime = data.checkedAt.toDate();
            const now = new Date();
            const ageInDays = (now - cachedTime) / (1000 * 60 * 60 * 24);
            
            if (ageInDays < 7) {
              setTrustData({
                trustScore: data.trustScore,
                summary: data.rawData ? {
                  suspicious: data.rawData.suspicious,
                  disposable: data.rawData.disposable,
                  freeProvider: data.rawData.details?.free_provider,
                  deliverable: data.rawData.details?.deliverable,
                  firstSeen: data.rawData.details?.first_seen,
                  profiles: data.rawData.details?.profiles || []
                } : {},
                recommendation: getTrustRecommendation(data.trustScore)
              });
            } else {
              // Data is too old, reverify
              verifyEmail(email, name);
            }
          } else {
            // No cached data, verify email
            verifyEmail(email, name);
          }
        } catch (err) {
          console.error("Error fetching cached verification data:", err);
          // Just log the error, don't show to user
        }
      };
      
      fetchCachedData();
    }
  }, [email]);

  // Function to get recommendation text based on score
  const getTrustRecommendation = (score) => {
    if (score >= 85) {
      return {
        level: 'high',
        text: 'Email appears to be legitimate with high confidence.'
      };
    } else if (score >= 65) {
      return {
        level: 'medium',
        text: 'Email seems generally trustworthy but may require verification.'
      };
    } else if (score >= 40) {
      return {
        level: 'low',
        text: 'Email has some suspicious characteristics. Consider requesting additional verification.'
      };
    } else {
      return {
        level: 'very-low',
        text: 'Email has multiple red flags. Strong verification recommended.'
      };
    }
  };

  // Function to verify an email
  const verifyEmail = async (email, name) => {
    if (!email) return;
    
    setIsVerifying(true);
    setError(null);
    
    try {
      const result = await checkEmailReputationFunction({ email, name });
      
      if (result.data) {
        setTrustData(result.data);
      } else {
        throw new Error("Verification returned no data");
      }
    } catch (err) {
      console.error("Error verifying email:", err);
      setError("Failed to verify email. Please try again later.");
    } finally {
      setIsVerifying(false);
    }
  };

  if (!email) {
    return (
      <div style={{
        textAlign: 'center',
        padding: '12px',
        backgroundColor: '#f8f9fa',
        borderRadius: '6px',
        border: '1px solid #eaedf3',
        color: '#818ea3',
        fontSize: '13px'
      }}>
        <p className="mb-0">Analyze Dispute</p>
      </div>
    );
  }

  return (
    <>
      <TrustScoreIndicator
        email={email}
        name={name}
        trustData={trustData}
        onVerify={verifyEmail}
        isVerifying={isVerifying}
      />
      
      {/* Error message hidden per request */}
      {false && error && (
        <div className="alert alert-danger mt-2" role="alert">
          {error}
        </div>
      )}
    </>
  );
};

export default TrustScoreContainer; 